import React, { useEffect, useState } from 'react';
import { X, FileText, Plus, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import procurementDocumentsService from '../../../services/procurementDocumentsService';
import RfqFormModal from './RfqFormModal';
import CompetitiveAnalysisFormModal from './CompetitiveAnalysisFormModal';
import PurchaseOrderFormModal from './PurchaseOrderFormModal';
import GrnFormModal from './GrnFormModal';
import DocumentDownloadButtons from './DocumentDownloadButtons';
import RfqDocumentView from './RfqDocumentView';
import CompetitiveAnalysisDocumentView from './CompetitiveAnalysisDocumentView';
import PurchaseOrderDocumentView from './PurchaseOrderDocumentView';
import GrnDocumentView from './GrnDocumentView';

const DOC_TYPES = [
  { key: 'rfq', label: 'Request for Quotation' },
  { key: 'ca', label: 'Competitive Analysis' },
  { key: 'po', label: 'Purchase Order' },
  { key: 'grn', label: 'Goods Received Note' },
];

const ProcurementDocumentsPanel = ({ purchaseRequest, onClose }) => {
  const [docs, setDocs] = useState({ rfq: [], ca: [], po: [], grn: [] });
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(null); // 'rfq' | 'ca' | 'po' | 'grn' | null
  const [viewDoc, setViewDoc] = useState(null); // { type, data } | null

  const loadDocs = async () => {
    try {
      setLoading(true);
      const [rfqRes, caRes, poRes, grnRes] = await Promise.all([
        procurementDocumentsService.listRfqs(purchaseRequest.id),
        procurementDocumentsService.listCompetitiveAnalyses(purchaseRequest.id),
        procurementDocumentsService.listPurchaseOrders(purchaseRequest.id),
        procurementDocumentsService.listGrns(purchaseRequest.id),
      ]);
      setDocs({
        rfq: rfqRes.data.data?.rfqs || [],
        ca: caRes.data.data?.analyses || [],
        po: poRes.data.data?.purchaseOrders || [],
        grn: grnRes.data.data?.grns || [],
      });
    } catch (error) {
      console.error('Load procurement documents error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseRequest.id]);

  const closeFormAndRefresh = () => {
    setOpenForm(null);
    loadDocs();
  };

  const numberFieldFor = (key) => ({ rfq: 'rfq_number', ca: 'reference_no', po: 'po_number', grn: 'grn_number' }[key]);

  const downloaders = {
    rfq: { docx: (id, name) => procurementDocumentsService.downloadRfqDocx(id, name) },
    ca: { docx: (id, name) => procurementDocumentsService.downloadCompetitiveAnalysisDocx(id, name) },
    po: { docx: (id, name) => procurementDocumentsService.downloadPurchaseOrderDocx(id, name) },
    grn: { docx: (id, name) => procurementDocumentsService.downloadGrnDocx(id, name) },
  };

  const viewFetchers = {
    rfq: async (id) => (await procurementDocumentsService.getRfq(id)).data.data.rfq,
    ca: async (id) => (await procurementDocumentsService.getCompetitiveAnalysis(id)).data.data.analysis,
    po: async (id) => (await procurementDocumentsService.getPurchaseOrder(id)).data.data.purchaseOrder,
    grn: async (id) => (await procurementDocumentsService.getGrn(id)).data.data.grn,
  };

  const handleView = async (key, id) => {
    try {
      const data = await viewFetchers[key](id);
      setViewDoc({ type: key, data });
    } catch (error) {
      console.error('Load document detail error:', error);
      toast.error('Failed to load document');
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
        <div className="relative top-10 mx-auto p-5 border w-full max-w-3xl shadow-lg rounded-md bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" />
              Procurement Documents - {purchaseRequest.request_id}
            </h3>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
              <X className="h-5 w-5" />
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-500 text-sm">Loading...</div>
          ) : (
            <div className="space-y-4">
              {DOC_TYPES.map(({ key, label }) => (
                <div key={key} className="border rounded-md p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-gray-800">{label}</h4>
                    <button
                      onClick={() => setOpenForm(key)}
                      className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                    >
                      <Plus className="h-3.5 w-3.5" /> New
                    </button>
                  </div>
                  {docs[key].length === 0 ? (
                    <p className="text-xs text-gray-400">None created yet</p>
                  ) : (
                    <div className="space-y-2">
                      {docs[key].map((d) => (
                        <div key={d.id} className="flex items-center justify-between bg-gray-50 rounded px-3 py-2">
                          <span className="text-xs font-medium text-gray-700">{d[numberFieldFor(key)]}</span>
                          <DocumentDownloadButtons
                            onView={() => handleView(key, d.id)}
                            onDownloadDocx={() => downloaders[key].docx(d.id, `${d[numberFieldFor(key)]}.docx`)}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {openForm === 'rfq' && <RfqFormModal purchaseRequest={purchaseRequest} onClose={closeFormAndRefresh} />}
      {openForm === 'ca' && <CompetitiveAnalysisFormModal purchaseRequest={purchaseRequest} onClose={closeFormAndRefresh} />}
      {openForm === 'po' && <PurchaseOrderFormModal purchaseRequest={purchaseRequest} onClose={closeFormAndRefresh} />}
      {openForm === 'grn' && <GrnFormModal purchaseRequest={purchaseRequest} onClose={closeFormAndRefresh} />}

      {viewDoc?.type === 'rfq' && <RfqDocumentView rfq={viewDoc.data} onClose={() => setViewDoc(null)} />}
      {viewDoc?.type === 'ca' && (
        <CompetitiveAnalysisDocumentView analysis={viewDoc.data} onClose={() => setViewDoc(null)} />
      )}
      {viewDoc?.type === 'po' && <PurchaseOrderDocumentView po={viewDoc.data} onClose={() => setViewDoc(null)} />}
      {viewDoc?.type === 'grn' && <GrnDocumentView grn={viewDoc.data} onClose={() => setViewDoc(null)} />}
    </>
  );
};

export default ProcurementDocumentsPanel;
